import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertTriangle, LogOut, Info, Trash2 } from 'lucide-react';
import { useScrollLock } from '../../hooks/useScrollLock';
import { useBackButton } from '../../hooks/useBackButton';

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose?: () => void;
  onCancel?: () => void;
  onConfirm: () => void | Promise<void> | Promise<unknown>;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info' | string;
  variant?: 'danger' | 'warning' | 'info' | 'primary' | string;
  icon?: React.ReactNode;
  isLoading?: boolean;
}

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  onClose: customOnClose,
  onCancel,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'warning',
  variant,
  icon: customIcon,
  isLoading = false
}) => {
  const onClose = customOnClose || onCancel || (() => {});
  const dialogRef = useRef<HTMLDivElement>(null);

  // Hardware back button support for closing modal on mobile
  useBackButton(isOpen && !isLoading, onClose);

  // Lock body scroll when open
  useScrollLock({
    isLocked: isOpen,
    preserveScrollbarWidth: true,
  });

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isLoading) {
        onClose();
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(event.target as Node) && !isLoading) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, isLoading]);

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const effectiveType = variant || type;
  const isLogoutAction = title.toLowerCase().includes('log out') || title.toLowerCase().includes('sign out');
  const isDeleteAction = title.toLowerCase().includes('delete') || effectiveType === 'danger';

  const getTypeStyles = () => {
    if (customIcon) {
      return {
        badgeBg: 'bg-gradient-to-br from-violet-600/15 via-rose-500/15 to-purple-600/15 border-violet-500/30 text-rose-500 dark:text-rose-400 shadow-[0_0_25px_rgba(244,63,94,0.15)]',
        icon: customIcon,
        confirmButton: 'bg-gradient-to-r from-red-600 via-rose-600 to-pink-600 hover:from-red-700 hover:via-rose-700 hover:to-pink-700 shadow-rose-500/25',
        confirmText: confirmText || 'Confirm'
      };
    }

    if (isLogoutAction) {
      return {
        badgeBg: 'bg-gradient-to-br from-violet-600/20 via-rose-500/20 to-purple-600/20 border-violet-500/30 dark:border-violet-400/30 text-rose-500 dark:text-rose-400 shadow-[0_0_25px_rgba(244,63,94,0.2)]',
        icon: <LogOut size={26} className="text-rose-500 dark:text-rose-400 translate-x-0.5" />,
        confirmButton: 'bg-gradient-to-r from-red-600 via-rose-600 to-pink-600 hover:from-red-700 hover:via-rose-700 hover:to-pink-700 shadow-lg shadow-rose-500/25',
        confirmText: confirmText || 'Log Out'
      };
    }

    switch (effectiveType) {
      case 'danger':
        return {
          badgeBg: 'bg-gradient-to-br from-red-600/20 via-rose-500/20 to-violet-600/20 border-red-500/30 dark:border-red-400/30 text-red-500 dark:text-red-400 shadow-[0_0_25px_rgba(239,68,68,0.2)]',
          icon: isDeleteAction ? <Trash2 size={26} className="text-red-500 dark:text-red-400" /> : <AlertTriangle size={26} className="text-red-500 dark:text-red-400" />,
          confirmButton: 'bg-gradient-to-r from-red-600 via-rose-600 to-pink-600 hover:from-red-700 hover:via-rose-700 hover:to-pink-700 shadow-lg shadow-red-500/25',
          confirmText: confirmText || 'Delete'
        };
      case 'warning':
        return {
          badgeBg: 'bg-gradient-to-br from-amber-500/20 to-violet-600/20 border-amber-500/30 dark:border-amber-400/30 text-amber-500 dark:text-amber-400 shadow-[0_0_25px_rgba(245,158,11,0.2)]',
          icon: <AlertTriangle size={26} className="text-amber-500 dark:text-amber-400" />,
          confirmButton: 'bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 hover:from-amber-600 hover:via-orange-600 hover:to-rose-600 shadow-lg shadow-amber-500/25',
          confirmText: confirmText || 'Proceed'
        };
      case 'info':
      default:
        return {
          badgeBg: 'bg-gradient-to-br from-violet-600/20 to-purple-600/20 border-violet-500/30 dark:border-violet-400/30 text-violet-500 dark:text-violet-300 shadow-[0_0_25px_rgba(139,92,246,0.2)]',
          icon: <Info size={26} className="text-violet-500 dark:text-violet-300" />,
          confirmButton: 'bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 shadow-lg shadow-violet-500/25',
          confirmText: confirmText || 'Confirm'
        };
    }
  };

  const typeStyles = getTypeStyles();

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md"
        >
          <motion.div
            ref={dialogRef}
            initial={{ opacity: 0, scale: 0.92, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 16 }}
            transition={{ type: 'spring', damping: 26, stiffness: 340 }}
            className="relative bg-white/95 dark:bg-[#1A1726] rounded-3xl shadow-2xl dark:shadow-[0_25px_70px_-15px_rgba(139,92,246,0.3)] w-full max-w-sm sm:max-w-md overflow-hidden border border-neutral-200/80 dark:border-violet-500/25 p-6 sm:p-7 text-center"
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              disabled={isLoading}
              className="absolute top-4 right-4 p-2 rounded-full text-neutral-400 hover:text-neutral-700 dark:text-violet-300/60 dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-violet-500/20 transition-colors disabled:opacity-40"
              aria-label="Close"
            >
              <X size={18} />
            </button>

            {/* Glowing Crevvi Icon Badge */}
            <div className="flex justify-center mb-4">
              <div className={`w-16 h-16 rounded-2xl border flex items-center justify-center shadow-lg transition-transform hover:scale-105 duration-200 ${typeStyles.badgeBg}`}>
                {typeStyles.icon}
              </div>
            </div>

            {/* Title & Message */}
            <h3 className="text-xl font-bold text-neutral-900 dark:text-white tracking-tight">
              {title}
            </h3>
            <p className="text-sm text-neutral-600 dark:text-violet-200/80 leading-relaxed mt-2.5 px-1 sm:px-2">
              {message}
            </p>

            {/* Action Buttons */}
            <div className="flex items-center gap-3 mt-7">
              <button
                type="button"
                disabled={isLoading}
                onClick={onClose}
                className="flex-1 py-2.5 px-4 text-sm font-semibold rounded-xl text-neutral-700 dark:text-violet-200 bg-neutral-100 hover:bg-neutral-200/90 dark:bg-violet-950/40 dark:hover:bg-violet-900/50 border border-neutral-200 dark:border-violet-500/20 transition-all active:scale-[0.98] disabled:opacity-50"
              >
                {cancelText}
              </button>
              <button
                type="button"
                disabled={isLoading}
                className={`flex-1 py-2.5 px-4 text-sm font-semibold text-white rounded-xl shadow-lg transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2 ${typeStyles.confirmButton}`}
                onClick={handleConfirm}
              >
                {isLoading && (
                  <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                <span>{isLoading ? 'Processing...' : typeStyles.confirmText}</span>
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};


